import { Box, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { NegotiationStateSVG } from '../../../components/NegotiationDebts/getConfirmationComponent';
import { useBrandContext } from '../../../dictionaries/context/BankContext';
import RenderText from '../../../dictionaries/RenderText';
import { Normative026, OutDebt } from '../../../generated/graphql';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../../utils/getNegotiationOptionLabel';
import { TranquiTheme } from '../../App/theme';
import { transformInfoBank } from '../transformInfoBank';

interface DebtDetailsNegotiationConfirmationStateImageProps {
  outDebt: OutDebt;
}

const DebtDetailsNegotiationConfirmationStateImage:
React.FC<DebtDetailsNegotiationConfirmationStateImageProps> = ({ outDebt }) => {
  const { type, state, alternative, negotiationOption } = outDebt.negotiation!;
  const {
    isApprovedByBank,
    isExpired,
    isRejected,
    isPartialPaymentPromise,
    isBogota, isBalanceBehindPayment,
    isScotiabank, isRefinance,
    isBbva,
  } = getNegBooleans(outDebt);
  const [ brand = '' ] = useBrandContext();
  const withTitle = isExpired;
  const withCaption = isApprovedByBank || isRejected || isPartialPaymentPromise;
  const bogotaBalanceBehindPaymentKey = (isBogota && isBalanceBehindPayment && alternative?.type) ? `_${alternative.type}` : '';
  const scotiabankRefinanceKey = (isScotiabank && isRefinance && (isRejected || isApprovedByBank)) ? `_${outDebt.extra?.normative026}` : '';
  const bbvaRefinanceKey = (isBbva && isRefinance && (isRejected || isApprovedByBank)) ? `_${outDebt.extra?.normative026}` : '';
  const subType = scotiabankRefinanceKey || bogotaBalanceBehindPaymentKey || bbvaRefinanceKey;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();

  return (
    <>
      <Box className={classes.sentImage}>{NegotiationStateSVG(state)}</Box>
      {withTitle && (
        <Typography variant="h1" className={cn(classes.title, 'mb-3')} data-test="cy-confirmationTitle-text">
          <RenderText
            textKey={`negotiation_${type}_confirmation_title_${state}`}
            isMarkdown
          />
        </Typography>
      )}
      {withCaption && (
        <>
          <Typography className={cn(classes.descriptionText, 'mb-3')}>
            <RenderText
              textKey={`${`negotiation_${type}_confirmation_caption_${state}${subType}`}`}
              context={{
                bank: transformInfoBank(outDebt).bank,
                negType: type,
                brand,
                issuingEntity: outDebt.extra?.issuingEntity || '',
                negTypeLabel: getNegotiationOptionLabel(
                  negotiationOption.type,
                  outDebt.bank,
                  outDebt.extra?.normative026 as Normative026,
                  alternative,
                  outDebt.brand,
                ),
              }}
              isMarkdown
            />
          </Typography>
        </>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  sentImage: {
    marginBottom: theme.spacing(6),
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...theme.typography.h4,
    color: theme.palette.indigo.main,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1.4rem',
    marginBottom: theme.spacing(1),
    whiteSpace: 'pre-line',
  },
  descriptionText: {
    ...theme.typography.body1,
    color: theme.palette.indigo.main,
    textAlign: 'center',
  },
}));

export default DebtDetailsNegotiationConfirmationStateImage;
